import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building, Mail, Phone, Globe, Calendar, FileText,
  Image, Users, Send, Plus, Edit2, Trash2, Search, Clock,
  CheckCircle, XCircle, Link2, Download, Eye, ChevronDown,
  ChevronRight, Grid, List, ExternalLink, Tag
} from 'lucide-react';
import axios from 'axios';
import PlanDetailModalRedesigned from './clients/plan-detail';
import AudiencesTab from './clients/AudiencesTab';
import AudienceFormModal from './clients/AudienceFormModal';
import PostTypesTab from './clients/PostTypesTab';
import SimplePlansTab from './clients/SimplePlansTab';
import ContentWorkflowTab from './clients/ContentWorkflowTab';
import ClientProfileTab from './clients/ClientProfileTab';
import WebsitesTab from './clients/WebsitesTab';
import ContentIdeasTab from './clients/ContentIdeasTab';
import BrandIdentityTab from './clients/BrandIdentityTab';
import ImportantNotesTab from './clients/ImportantNotesTab';
import SitemapTab from './clients/SitemapTab';
import BrandAssetsTab from './clients/BrandAssetsTab';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface Client {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
}

interface Contact {
  id: number;
  client_id: number;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  is_primary: boolean;
  can_approve: boolean;
  notes?: string;
}

interface Plan {
  id: number;
  type: 'weekly' | 'monthly';
  name: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  client_id?: number;
  items?: ContentItem[];
  item_count?: number;
  approval_status?: string;
}

interface ContentItem {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  assets?: Asset[];
  asset_count?: number;
}

interface Asset {
  id: number;
  content_id?: number;
  asset_type: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  r2_key: string;
  created_at: string;
}

interface ReviewLink {
  id: number;
  link_code: string;
  reviewer_type: 'account_manager' | 'client';
  content_id?: number;
  plan_id?: number;
  client_id?: number;
  created_at: string;
}

export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'profile' | 'team' | 'content-marketing' | 'links'>('profile');
  const [profileSubTab, setProfileSubTab] = useState<'brand' | 'notes' | 'sitemap' | 'assets'>('brand');
  const [teamSubTab, setTeamSubTab] = useState<'contacts' | 'audiences'>('contacts');
  const [contentMarketingSubTab, setContentMarketingSubTab] = useState<'websites' | 'workflow' | 'post-types' | 'plans'>('websites');
  const [client, setClient] = useState<Client | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [reviewLinks, setReviewLinks] = useState<ReviewLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPlans, setExpandedPlans] = useState<Set<number>>(new Set());

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showGenerateLinksModal, setShowGenerateLinksModal] = useState(false);
  const [showPlanDetailModal, setShowPlanDetailModal] = useState(false);
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editingAudience, setEditingAudience] = useState<any>(null);
  const [selectedPlanForLinks, setSelectedPlanForLinks] = useState<Plan | null>(null);
  const [selectedPlanForDetail, setSelectedPlanForDetail] = useState<Plan | null>(null);

  // Form data
  const [planForm, setPlanForm] = useState({
    name: '',
    type: 'monthly' as 'weekly' | 'monthly',
    start_date: '',
    end_date: '',
    status: 'draft' as Plan['status']
  });


  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    is_primary: false,
    can_approve: true,
    notes: ''
  });

  useEffect(() => {
    // Set auth token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    if (clientId) {
      loadClientData();
    }
  }, [clientId, activeTab]);

  const loadClientData = async () => {
    try {
      setLoading(true);

      // Load client details
      const clientRes = await axios.get(`${API_BASE_URL}/clients/${clientId}`);
      setClient(clientRes.data);

      // Load data based on active tab
      switch (activeTab) {
        case 'overview':
          await Promise.all([
            loadPlans(),
            loadContacts()
          ]);
          break;
        case 'plans':
          await loadPlans();
          break;
        case 'contacts':
          await loadContacts();
          break;
        case 'audiences':
          await loadAudiences();
          break;
        case 'simple-plans':
          // Simple plans will be loaded by the component itself
          break;
        case 'post-types':
          // Post types will be loaded by the component itself
          break;
        case 'links':
          await loadReviewLinks();
          break;
      }
    } catch (error) {
      console.error('Failed to load client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/plans?client_id=${clientId}`);
      const plansData = response.data || [];

      // Load content items for each plan
      const plansWithItems = await Promise.all(
        plansData.map(async (plan: Plan) => {
          try {
            const itemsRes = await axios.get(`${API_BASE_URL}/content?plan_id=${plan.id}`);
            // Handle the API response structure { items: [], total: ..., limit: ..., offset: ... }
            const items = itemsRes.data.items || itemsRes.data || [];
            return {
              ...plan,
              items: Array.isArray(items) ? items : [],
              item_count: Array.isArray(items) ? items.length : 0
            };
          } catch {
            return { ...plan, items: [], item_count: 0 };
          }
        })
      );

      setPlans(plansWithItems);
    } catch (error) {
      console.error('Failed to load plans:', error);
      setPlans([]);
    }
  };


  const loadContacts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contacts?client_id=${clientId}`);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      setContacts([]);
    }
  };

  const loadReviewLinks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/review/links?client_id=${clientId}`);
      setReviewLinks(response.data || []);
    } catch (error) {
      console.error('Failed to load review links:', error);
      setReviewLinks([]);
    }
  };

  const loadAudiences = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/audiences?client_id=${clientId}`);
      setAudiences(response.data.audiences || []);
    } catch (error) {
      console.error('Failed to load audiences:', error);
      setAudiences([]);
    }
  };

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const planData = {
        ...planForm,
        client_id: parseInt(clientId!)
      };

      if (editingPlan) {
        await axios.put(`${API_BASE_URL}/plans/${editingPlan.id}`, planData);
      } else {
        await axios.post(`${API_BASE_URL}/plans`, planData);
      }

      await loadPlans();
      handleClosePlanModal();
    } catch (error) {
      console.error('Failed to save plan:', error);
      alert('Failed to save plan');
    }
  };


  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const contactData = {
        ...contactForm,
        client_id: parseInt(clientId!)
      };

      if (editingContact) {
        await axios.put(`${API_BASE_URL}/contacts/${editingContact.id}`, contactData);
      } else {
        await axios.post(`${API_BASE_URL}/contacts`, contactData);
      }

      await loadContacts();
      handleCloseContactModal();
    } catch (error) {
      console.error('Failed to save contact:', error);
      alert('Failed to save contact');
    }
  };

  const handleGenerateLinks = async () => {
    if (!selectedPlanForLinks) return;

    try {
      const response = await axios.post(`${API_BASE_URL}/review/generate-links`, {
        plan_id: selectedPlanForLinks.id,
        client_id: parseInt(clientId!)
      });

      alert(
        `Review links generated successfully!\n\n` +
        `Account Manager Link:\n${response.data.account_manager_link}\n\n` +
        `Client Link:\n${response.data.client_link}`
      );

      setShowGenerateLinksModal(false);
      setSelectedPlanForLinks(null);
      await loadReviewLinks();
    } catch (error) {
      console.error('Failed to generate links:', error);
      alert('Failed to generate review links');
    }
  };

  const handleDeletePlan = async (planId: number) => {
    if (confirm('Are you sure you want to delete this plan?')) {
      try {
        await axios.delete(`${API_BASE_URL}/plans/${planId}`);
        await loadPlans();
      } catch (error) {
        console.error('Failed to delete plan:', error);
        alert('Failed to delete plan');
      }
    }
  };



  const handleDeleteContact = async (contactId: number) => {
    if (confirm('Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API_BASE_URL}/contacts/${contactId}`);
        await loadContacts();
      } catch (error) {
        console.error('Failed to delete contact:', error);
        alert('Failed to delete contact');
      }
    }
  };

  const togglePlanExpansion = (planId: number) => {
    const newExpanded = new Set(expandedPlans);
    if (newExpanded.has(planId)) {
      newExpanded.delete(planId);
    } else {
      newExpanded.add(planId);
    }
    setExpandedPlans(newExpanded);
  };

  const handleClosePlanModal = () => {
    setShowPlanModal(false);
    setEditingPlan(null);
    setPlanForm({
      name: '',
      type: 'monthly',
      start_date: '',
      end_date: '',
      status: 'draft'
    });
  };


  const handleCloseContactModal = () => {
    setShowContactModal(false);
    setEditingContact(null);
    setContactForm({
      name: '',
      email: '',
      phone: '',
      role: '',
      is_primary: false,
      can_approve: true,
      notes: ''
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      pending_review: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      completed: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getApprovalIcon = (status?: string) => {
    if (!status) return <Clock className="w-4 h-4 text-gray-400" />;
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading client details...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-500">Client not found</p>
          <button
            onClick={() => navigate('/dashboard/clients')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header with back button and client info */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/clients')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Building className="w-8 h-8 text-blue-600" />
              </div>
              <div className="ml-6">
                <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                {client.company && (
                  <p className="text-gray-600 mt-1">{client.company}</p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm">
                  {client.email && (
                    <a href={`mailto:${client.email}`} className="flex items-center text-gray-600 hover:text-blue-600">
                      <Mail className="w-4 h-4 mr-1" />
                      {client.email}
                    </a>
                  )}
                  {client.phone && (
                    <a href={`tel:${client.phone}`} className="flex items-center text-gray-600 hover:text-blue-600">
                      <Phone className="w-4 h-4 mr-1" />
                      {client.phone}
                    </a>
                  )}
                  {client.website && (
                    <a href={client.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-600 hover:text-blue-600">
                      <Globe className="w-4 h-4 mr-1" />
                      Website
                    </a>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate(`/dashboard/clients/${clientId}/edit`)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <Edit2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Client Profile
            </button>
            <button
              onClick={() => setActiveTab('team')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'team'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Team & Contacts
            </button>
            <button
              onClick={() => setActiveTab('content-marketing')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'content-marketing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                Content Marketing
              </div>
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'contacts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Contacts
              </div>
            </button>
            <button
              onClick={() => setActiveTab('audiences')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audiences'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Audiences
              </div>
            </button>
            <button
              onClick={() => setActiveTab('links')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'links'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Link2 className="w-4 h-4 mr-2" />
                Review Links
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Client Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              {/* Profile Sub Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setProfileSubTab('brand')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      profileSubTab === 'brand'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Brand Identity
                  </button>
                  <button
                    onClick={() => setProfileSubTab('notes')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      profileSubTab === 'notes'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Important Notes
                  </button>
                  <button
                    onClick={() => setProfileSubTab('sitemap')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      profileSubTab === 'sitemap'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Sitemap
                  </button>
                  <button
                    onClick={() => setProfileSubTab('assets')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      profileSubTab === 'assets'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Brand Assets
                  </button>
                </nav>
              </div>

              {/* Profile Sub Tab Content */}
              {profileSubTab === 'brand' && <BrandIdentityTab clientId={parseInt(clientId!)} />}
              {profileSubTab === 'notes' && <ImportantNotesTab clientId={parseInt(clientId!)} />}
              {profileSubTab === 'sitemap' && <SitemapTab clientId={parseInt(clientId!)} />}
              {profileSubTab === 'assets' && <BrandAssetsTab clientId={parseInt(clientId!)} />}
            </div>
          )}

          {/* Team & Contacts Tab */}
          {activeTab === 'team' && (
            <div>
              {/* Team Sub Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setTeamSubTab('contacts')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      teamSubTab === 'contacts'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Contacts
                  </button>
                  <button
                    onClick={() => setTeamSubTab('audiences')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      teamSubTab === 'audiences'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Ad Audiences
                  </button>
                </nav>
              </div>

              {/* Contacts Sub Tab */}
              {teamSubTab === 'contacts' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Client Contacts</h3>
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Contact
                  </button>
                </div>

                {contacts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No contacts found for this client
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {contacts.map((contact) => (
                          <tr key={contact.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-medium text-gray-900">{contact.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{contact.role || '—'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {contact.email ? (
                                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline text-sm">
                                  {contact.email}
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {contact.phone ? (
                                <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline text-sm">
                                  {contact.phone}
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex gap-1">
                                {contact.is_primary && (
                                  <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Primary</span>
                                )}
                                {contact.can_approve && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Approver</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => {
                                  setEditingContact(contact);
                                  setContactForm({
                                    name: contact.name,
                                    email: contact.email || '',
                                    phone: contact.phone || '',
                                    role: contact.role || '',
                                    is_primary: contact.is_primary,
                                    can_approve: contact.can_approve,
                                    notes: contact.notes || ''
                                  });
                                  setShowContactModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-700 mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Audiences Sub Tab */}
              {teamSubTab === 'audiences' && (
                <AudiencesTab
                  clientId={parseInt(clientId!)}
                  onOpenAudienceModal={(audience) => {
                    setEditingAudience(audience || null);
                    setShowAudienceModal(true);
                  }}
                />
              )}
            </div>
          )}

          {/* Content Marketing Tab */}
          {activeTab === 'content-marketing' && (
            <div>
              {/* Sub Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setContentMarketingSubTab('websites')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      contentMarketingSubTab === 'websites'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Websites
                  </button>
                  <button
                    onClick={() => setContentMarketingSubTab('workflow')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      contentMarketingSubTab === 'workflow'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Content Workflow
                  </button>
                  <button
                    onClick={() => setContentMarketingSubTab('post-types')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      contentMarketingSubTab === 'post-types'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Post Types (Advanced)
                  </button>
                  <button
                    onClick={() => setContentMarketingSubTab('plans')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      contentMarketingSubTab === 'plans'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Content Plans
                  </button>
                </nav>
              </div>

              {/* Sub Tab Content */}
              {contentMarketingSubTab === 'websites' && (
                <WebsitesTab clientId={parseInt(clientId!)} />
              )}

              {contentMarketingSubTab === 'workflow' && (
                <ContentWorkflowTab clientId={parseInt(clientId!)} />
              )}

              {contentMarketingSubTab === 'post-types' && (
                <PostTypesTab clientId={parseInt(clientId!)} />
              )}

              {contentMarketingSubTab === 'plans' && (
                <div>
              <div className="flex justify-between items-center mb-4">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search plans..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={() => setShowPlanModal(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Plan
                </button>
              </div>

              <div className="space-y-4">
                {plans.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No plans found for this client
                  </div>
                ) : (
                  plans.map((plan) => (
                    <div key={plan.id} className="border border-gray-200 rounded-lg">
                      <div
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          setSelectedPlanForDetail(plan);
                          setShowPlanDetailModal(true);
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                          <div>
                            <h3 className="font-medium text-gray-900">{plan.name}</h3>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-sm text-gray-600">
                                {plan.type === 'weekly' ? 'Weekly' : 'Monthly'}
                              </span>
                              <span className="text-sm text-gray-600">
                                {new Date(plan.start_date).toLocaleDateString()} - {new Date(plan.end_date).toLocaleDateString()}
                              </span>
                              <span className="text-sm text-gray-600">
                                {plan.item_count || 0} items
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(plan.status)}`}>
                                {plan.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => {
                              setSelectedPlanForLinks(plan);
                              setShowGenerateLinksModal(true);
                            }}
                            className="p-2 text-blue-600 hover:text-blue-700"
                            title="Generate Review Links"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingPlan(plan);
                              setPlanForm({
                                name: plan.name,
                                type: plan.type,
                                start_date: plan.start_date,
                                end_date: plan.end_date,
                                status: plan.status
                              });
                              setShowPlanModal(true);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePlan(plan.id)}
                            className="p-2 text-red-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
                </div>
              )}
            </div>
          )}

          {/* Review Links Tab */}
          {activeTab === 'links' && (
            <div>
              {reviewLinks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No review links generated yet</p>
                  <p className="mt-2 text-sm">Generate links from the Plans tab</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Link Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reviewer Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reviewLinks.map((link) => (
                      <tr key={link.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {link.link_code}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            link.reviewer_type === 'account_manager'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {link.reviewer_type === 'account_manager' ? 'Account Manager' : 'Client'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(link.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              const url = `${window.location.origin}/review/${link.link_code}`;
                              navigator.clipboard.writeText(url);
                              alert('Link copied to clipboard!');
                            }}
                            className="text-blue-600 hover:text-blue-700 mr-3"
                          >
                            Copy Link
                          </button>
                          <a
                            href={`/review/${link.link_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <ExternalLink className="w-4 h-4 inline" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingPlan ? 'Edit Plan' : 'Create New Plan'}
            </h3>

            <form onSubmit={handleSavePlan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={planForm.type}
                  onChange={(e) => setPlanForm({ ...planForm, type: e.target.value as 'weekly' | 'monthly' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={planForm.start_date}
                    onChange={(e) => setPlanForm({ ...planForm, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={planForm.end_date}
                    onChange={(e) => setPlanForm({ ...planForm, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClosePlanModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingPlan ? 'Update' : 'Create'} Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </h3>

            <form onSubmit={handleSaveContact} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input
                  type="text"
                  value={contactForm.role}
                  onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                  placeholder="e.g., Marketing Director"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={contactForm.is_primary}
                    onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Primary contact</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={contactForm.can_approve}
                    onChange={(e) => setContactForm({ ...contactForm, can_approve: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Can approve content</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={contactForm.notes}
                  onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseContactModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingContact ? 'Update' : 'Add'} Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Links Modal */}
      {showGenerateLinksModal && selectedPlanForLinks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Generate Review Links</h3>
            <p className="text-gray-600 mb-6">
              Generate review links for "{selectedPlanForLinks.name}"?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGenerateLinksModal(false);
                  setSelectedPlanForLinks(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateLinks}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Generate Links
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Detail Modal */}
      <PlanDetailModalRedesigned
        isOpen={showPlanDetailModal}
        plan={selectedPlanForDetail}
        onClose={() => {
          setShowPlanDetailModal(false);
          setSelectedPlanForDetail(null);
        }}
      />

      {/* Audience Form Modal */}
      <AudienceFormModal
        isOpen={showAudienceModal}
        onClose={() => {
          setShowAudienceModal(false);
          setEditingAudience(null);
        }}
        clientId={parseInt(clientId!)}
        audience={editingAudience}
        onSuccess={() => {
          loadAudiences();
          setShowAudienceModal(false);
          setEditingAudience(null);
        }}
      />
    </div>
  );
}